import { AttributionEffectEventType as AttributionOutcomeType } from "@redotech/crdb-enums/attribution/attribution-effect-event-type";
import {
  AttributionEvidenceType,
  type AttributionSettings,
  type Duration,
} from "@redotech/redo-attribution-common/attribution-settings";
import { durationToSeconds, TimeUnit } from "@redotech/redo-common-model/time";
import {
  marketingAttributionEvent,
  type MarketingAttributionEvent,
} from "@redotech/redo-marketing-templates-common/marketing";

/** What each checkbox on the page means. An SMS send counts as delivered,
 *  since carriers don't report SMS delivery. */
const EVIDENCE_BY_EVENT: Partial<
  Record<MarketingAttributionEvent, AttributionEvidenceType[]>
> = {
  [marketingAttributionEvent.CLICK]: [
    AttributionEvidenceType.EMAIL_CLICK,
    AttributionEvidenceType.SMS_CLICK,
  ],
  [marketingAttributionEvent.OPEN]: [AttributionEvidenceType.EMAIL_OPEN],
  [marketingAttributionEvent.DELIVERY]: [
    AttributionEvidenceType.EMAIL_DELIVERED,
    AttributionEvidenceType.SMS_SEND,
  ],
};

const FILTERABLE_EVIDENCE = new Set(Object.values(EVIDENCE_BY_EVENT).flat());

/** The attribution window the marketing analytics surfaces fall back to. */
export const DEFAULT_ATTRIBUTION_DAYS = 5;

/** The day counts the dropdown offers. */
export const SELECTABLE_ATTRIBUTION_WINDOWS = [
  1, 2, 3, 4, 5, 7, 10, 14, 21, 30,
] as const;

/**
 * The team's saved settings with the analytics page's filters applied. Evidence
 * types the page cannot name keep whatever the settings say.
 */
export function analyticsAttributionSettings({
  settings,
  attributionDays,
  attributionEvents,
}: {
  settings: AttributionSettings;
  attributionDays: number;
  attributionEvents: readonly MarketingAttributionEvent[];
}): AttributionSettings {
  const selected = new Set(
    attributionEvents.flatMap((event) => EVIDENCE_BY_EVENT[event] ?? []),
  );
  const window: Duration = { value: attributionDays, unit: TimeUnit.DAY };
  const order = settings[AttributionOutcomeType.Order];
  const evidence = { ...order.evidence };
  for (const evidenceType of FILTERABLE_EVIDENCE) {
    const config = evidence[evidenceType];
    if (config) {
      evidence[evidenceType] = {
        ...config,
        enabled: selected.has(evidenceType),
        window,
      };
    }
  }
  return { [AttributionOutcomeType.Order]: { ...order, evidence } };
}

const windowInDays = ({ value, unit }: Duration) =>
  durationToSeconds(value, unit) / durationToSeconds(1, TimeUnit.DAY);

/** A saved window the dropdown can't show would leave it blank, so snap to
 *  the closest one it can. */
function nearestSelectableWindow(days: number): number {
  return SELECTABLE_ATTRIBUTION_WINDOWS.reduce((best, value) =>
    Math.abs(value - days) < Math.abs(best - days) ? value : best,
  );
}

/** Every checkbox the page offers. */
export const FILTERABLE_ATTRIBUTION_EVENTS = Object.values(
  marketingAttributionEvent,
).filter((event) => EVIDENCE_BY_EVENT[event] !== undefined);

/** What the page opens with, taken from the team's attribution settings. */
export interface AnalyticsAttributionFilters {
  attributionDays: number;
  attributionEvents: MarketingAttributionEvent[];
}

export function analyticsFiltersFromSettings(
  settings: AttributionSettings,
): AnalyticsAttributionFilters {
  const order = settings[AttributionOutcomeType.Order];
  const attributionEvents = FILTERABLE_ATTRIBUTION_EVENTS.filter((event) =>
    (EVIDENCE_BY_EVENT[event] ?? []).some(
      (evidenceType) => order.evidence[evidenceType]?.enabled,
    ),
  );

  // Settings store a window per evidence type, but the page has one dropdown,
  // so show the longest of the ones it controls.
  const windows = [...FILTERABLE_EVIDENCE].flatMap((evidenceType) => {
    const config = order.evidence[evidenceType];
    return config?.enabled ? [windowInDays(config.window)] : [];
  });
  // A window can be saved in hours, shorter than any option.
  const longest = windows.length
    ? Math.max(...windows)
    : DEFAULT_ATTRIBUTION_DAYS;
  return {
    attributionDays: nearestSelectableWindow(longest),
    attributionEvents,
  };
}
